import React from "react";
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
  useParams,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import MyProfile from "./pages/MyAccount/MyProfile";
import AppearancePage from "./pages/MyAccount/AppearancePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import MfaSetupPage from "./pages/MfaSetupPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import SignupCompletePage from "./pages/SignupCompletePage";
import SignupPage from "./pages/SignupPage";
import DeviceAuthApprovalPage from "./pages/DeviceAuthApprovalPage";
import ProtectedLayout from "./components/ProtectedLayout";
import PublicLayout from "./components/PublicLayout";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import ClientManagementPage from "./pages/ClientManagementPage";
import CreateClientPage from "./pages/CreateClientPage";
import ClientConfigurationPage from "./pages/ClientConfigurationPage";
import UserManagementPage from "./pages/UserManagementPage";
import ProjectsPage from "./pages/ProjectsPage";
import TestCaseManagementPage from "./pages/TestCaseManagementPage";
import TestRunManagementPage from "./pages/TestRunManagementPage";
import NewTestRunPage from "./pages/NewTestRunPage";
import TestRunExecutionPage from "./pages/TestRunExecutionPage";
import TestPlansPage from "./modules/testPlan/pages/TestPlanList";
import TestPlanCreatePage from "./modules/testPlan/pages/CreateTestPlan";
import ReviewTestCasesPage from "./pages/ReviewTestCasesPage";
import SharedStepsPage from "./pages/SharedStepsPage";
import SharedStepCreatePage from "./pages/SharedStepCreatePage";
import ScreenRecorderPage from "./pages/ScreenRecorderPage";
import ProjectDashboardPage from "./pages/ProjectDashboardPage";
import EditUserPage from "./pages/EditUserPage";
import SettingsPage from "./pages/SettingsPage";
import CustomizationPage from "./pages/CustomizationPage";
import CreateTemplatePage from "./pages/CreateTemplatePage";
import CreateFieldPage from "./pages/CreateFieldPage";
import EditFieldPage from "./pages/EditFieldPage";
import SuiteManagementPage from "./pages/SuiteManagementPage";
import BaselineManagementPage from "./pages/BaselineManagementPage";
import BranchManagementPage from "./pages/BranchManagementPage";
import ProjectSettingsPage from "./pages/ProjectSettingsPage";
import DataManagementPage from "./pages/DataManagementPage";
import MigrationPage from "./pages/MigrationPage";
import MigrationWizardPage from "./pages/MigrationWizardPage";
import DefectsPage from "./pages/DefectsPage";
import Support from "./pages/Support";
import Documentation from "./pages/Documentation";
import TestCaseCreatePage from "./pages/TestCaseCreatePage";
import TestCaseHistoryPage from "./pages/TestCaseHistoryPage";
import CreateProjectWizard from "./components/CreateProjectWizard";
import AIAssistantPage from "./pages/AIAssistantPage.jsx";
import { useUmami } from "./hooks/useUmami";
import TopLoader from "./components/TopLoader";
import QueryFetchingProgress from "./components/QueryFetchingProgress";
import RouteLoader from "./components/layout/RouteLoader";
import { WorkspaceTabsProvider } from "./context/WorkspaceTabsContext";
import { isElectronApp } from "./utils/utils";

function UmamiSpaTracker({ children }) {
  useUmami();
  return children;
}

function HistoryStepsLegacyRedirect() {
  const { search } = useLocation();
  const target = new URLSearchParams(search);
  target.set("tab", "test_step");
  return <Navigate to={`/test-cases/history?${target.toString()}`} replace />;
}

function TestPlanIdToEditRedirect() {
  const { planId } = useParams();
  const { search } = useLocation();
  if (!planId || planId === "edit" || planId === "create") {
    return <Navigate to="/test-plans" replace />;
  }
  return <Navigate to={`/test-plans/edit/${planId}${search}`} replace />;
}

function AppShell() {
  const shell = (
    <UmamiSpaTracker>
      <div className="App min-h-screen">
        <Toaster position="top-right" />
        <TopLoader />
        <QueryFetchingProgress />
        <div className="flex flex-col">
          <Outlet />
        </div>
      </div>
    </UmamiSpaTracker>
  );

  return isElectronApp() ? (
    <WorkspaceTabsProvider>{shell}</WorkspaceTabsProvider>
  ) : (
    shell
  );
}

export const appRouter = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      {
        element: <RouteLoader />,
        children: [
          {
            element: <PublicLayout />,
            children: [
              { index: true, element: <LandingPage /> },
              { path: "login", element: <LoginPage /> },
              { path: "device-auth/approve", element: <DeviceAuthApprovalPage /> },
              { path: "auth/mfa/setup", element: <MfaSetupPage /> },
              { path: "forgot-password", element: <ForgotPasswordPage /> },
              { path: "change-password", element: <ChangePasswordPage /> },
              { path: "invite/:token", element: <InviteAcceptPage /> },
              { path: "register", element: <SignupPage /> },
              { path: "signup/complete", element: <SignupCompletePage /> },
            ],
          },
          {
            element: <ProtectedLayout />,
            children: [
              { path: "dashboard", element: <ProjectDashboardPage /> },
              { path: "support", element: <Support /> },
              {
                path: "clients/create",
                element: (
                  <RoleProtectedRoute
                    allowedRoles="chief-admin"
                    redirectTo="/projects"
                  >
                    <CreateClientPage />
                  </RoleProtectedRoute>
                ),
              },
              {
                path: "clients/*",
                element: (
                  <RoleProtectedRoute
                    allowedRoles="chief-admin"
                    redirectTo="/projects"
                  >
                    <ClientManagementPage />
                  </RoleProtectedRoute>
                ),
              },
              {
                path: "client-configuration",
                element: <ClientConfigurationPage />,
              },
              { path: "users", element: <UserManagementPage /> },
              { path: "documentation", element: <Documentation /> },
              { path: "users/edit/:userId", element: <EditUserPage /> },
              { path: "settings", element: <SettingsPage /> },

              { path: "templates/create", element: <CreateTemplatePage /> },
              { path: "fields/create", element: <CreateFieldPage /> },
              { path: "fields/edit/:fieldId", element: <EditFieldPage /> },
              { path: "projects/*", element: <ProjectsPage /> },
              {
                path: "create-project/*",
                element: <CreateProjectWizard isOpen={true} />,
              },
              { path: "test-cases", element: <TestCaseManagementPage /> },
              {
                path: "test-cases/create",
                element: <TestCaseCreatePage viewMode={false} />,
              },
              {
                path: "test-cases/view/:testCaseId",
                element: <TestCaseCreatePage viewMode={true} />,
              },
              {
                path: "test-cases/history/steps",
                element: <HistoryStepsLegacyRedirect />,
              },
              {
                path: "test-cases/history",
                element: <TestCaseHistoryPage />,
              },
              {
                path: "test-cases/history/:testCaseId",
                element: <TestCaseHistoryPage />,
              },
              { path: "test-runs", element: <TestRunManagementPage /> },
              { path: "test-runs/new", element: <NewTestRunPage /> },
              { path: "test-plans", element: <TestPlansPage /> },
              { path: "test-plans/create", element: <TestPlanCreatePage /> },
              {
                path: "test-plans/edit/:id",
                element: <TestPlanCreatePage />,
              },
              {
                path: "test-plans/:planId",
                element: <TestPlanIdToEditRedirect />,
              },
              {
                path: "test-run-execution",
                element: <TestRunExecutionPage />,
              },
              { path: "ai-assistant", element: <AIAssistantPage /> },
              {
                path: "review-test-cases",
                element: <ReviewTestCasesPage />,
              },
              { path: "suites", element: <SuiteManagementPage /> },
              { path: "baselines", element: <BaselineManagementPage /> },
              { path: "branches", element: <BranchManagementPage /> },
              { path: "shared-steps", element: <SharedStepsPage /> },
              {
                path: "shared-steps/create",
                element: <SharedStepCreatePage />,
              },
              {
                path: "shared-steps/edit/:sharedStepId",
                element: <SharedStepCreatePage />,
              },
              {
                path: "shared-steps/view/:sharedStepId",
                element: <SharedStepCreatePage />,
              },
              { path: "screen-recorder", element: <ScreenRecorderPage /> },
              {
                path: "project-settings",
                element: <ProjectSettingsPage />,
              },
              {
                path: "data-management",
                element: <DataManagementPage />,
              },
              { path: "migration", element: <MigrationPage /> },
              {
                path: "migration/wizard",
                element: <MigrationWizardPage />,
              },
              { path: "defects", element: <DefectsPage /> },
              { path: "my-account", element: <MyProfile /> },
              {
                path: "my-account/edit",
                element: <Navigate to="/my-account" replace />,
              },
              {
                path: "my-account/change-password",
                element: <Navigate to="/my-account" replace />,
              },
              {
                path: "my-account/appearance",
                element: <AppearancePage />,
              },
            ],
          },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
